import React, { useEffect, useMemo, useState, memo } from 'react';
import { SessionCard } from './SessionCard';
import { MySchedule } from './MySchedule';
import { Badge } from './ui/Badge';
import { Card } from './ui/Card';
import { CalendarIcon, ClockIcon, WifiOffIcon, ChevronDownIcon, CalendarDaysIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { useMySchedule } from '../contexts/MyScheduleContext';
import { Session, EventFeature } from '../types';
import { sessions as sessionsData } from '../data/sessions';
interface AgendaProps {
  onNavigateToSchedule?: () => void;
}
export function Agenda({
  onNavigateToSchedule
}: AgendaProps) {
  const {
    currentEvent,
    isOnline
  } = useEvent();
  const {
    bookedSessions,
    addBookedSession,
    removeBookedSession
  } = useMySchedule();
  const [selectedDay, setSelectedDay] = useState('all');
  const [selectedFeature, setSelectedFeature] = useState<string>('all');
  const [offlineBookings, setOfflineBookings] = useState(0);
  const [showMySchedule, setShowMySchedule] = useState(false);
  const [showFeatureDropdown, setShowFeatureDropdown] = useState(false);
  const [sessions, setSessions] = useState<Session[]>(sessionsData);
  // Event-specific features
  const getEventFeatures = (): EventFeature[] => {
    switch (currentEvent.id) {
      case 'industry':
        return [{
          id: 'all',
          name: 'All Sessions',
          description: '',
          eventId: 'industry'
        }, {
          id: 'me-fitness',
          name: 'ME Fitness Business Summit',
          description: 'Industry giants come together to elevate their leadership game while fostering a thriving culture of health and success.',
          eventId: 'industry'
        }, {
          id: 'future-wellness',
          name: 'Future Wellness Summit',
          description: 'A pivotal platform bringing together cutting-edge research, visionary ideas, and transformative practices.',
          eventId: 'industry'
        }, {
          id: 'xpro',
          name: 'XPRO Summit',
          description: 'Comprehensive 3-day programme empowering fitness professionals with cutting-edge knowledge and hands-on training.',
          eventId: 'industry'
        }, {
          id: 'mena-roundtable',
          name: 'MENA Roundtable',
          description: 'Eight industry leaders reimagining urban wellness and fitness as a force for healthier communities.',
          eventId: 'industry'
        }];
      case 'active':
        return [{
          id: 'all',
          name: 'All Sessions',
          description: '',
          eventId: 'active'
        }, {
          id: 'active-arena',
          name: 'Active Arena',
          description: 'Immerse yourself in heart-pounding energy with electrifying workouts led by headline talent.',
          eventId: 'active'
        }, {
          id: 'main-stage',
          name: 'Main Fitness Stage',
          description: 'Dynamic hub where incredible fitness activities and performances ignite passion for all levels.',
          eventId: 'active'
        }, {
          id: 'yoga-pilates',
          name: 'Yoga & Pilates Studio',
          description: 'Discover tranquility and transformation with expert instructors in a serene ambiance.',
          eventId: 'active'
        }, {
          id: 'dubai-active-talks',
          name: 'Dubai Active Talks',
          description: 'Q&As with headline talent and experts sharing insights on nutrition, mindfulness, and fitness.',
          eventId: 'active'
        }];
      case 'muscle':
        return [{
          id: 'all',
          name: 'All Sessions',
          description: '',
          eventId: 'muscle'
        }, {
          id: 'muscle-classic',
          name: 'Dubai Muscle Classic',
          description: "Middle East's biggest bodybuilding competition with IFBB Pro Elite. Over AED70,000 in prizes!",
          eventId: 'muscle'
        }, {
          id: 'muscle-talks',
          name: 'Dubai Muscle Talks',
          description: 'Q&A sessions with Mr. Olympia champions sharing training methods and mindset techniques.',
          eventId: 'muscle'
        }, {
          id: 'fmg',
          name: 'FMG International',
          description: "Women's bodybuilding competition showcasing fitness, muscle and glamour.",
          eventId: 'muscle'
        }, {
          id: 'team-nogueira',
          name: 'Team Nogueira Future Champions',
          description: "Competitions showcasing the region's best Boxing, MMA, Muay Thai and Jiu-Jitsu.",
          eventId: 'muscle'
        }, {
          id: 'meet-greet',
          name: 'Athlete Meet & Greet',
          description: 'Meet bodybuilding heroes for autographs, interactions and pictures.',
          eventId: 'muscle'
        }];
      default:
        return [{
          id: 'all',
          name: 'All Sessions',
          description: '',
          eventId: currentEvent.id
        }];
    }
  };
  const features = getEventFeatures();
  const selectedFeatureData = features.find(f => f.id === selectedFeature);
  const getFeatureName = (featureId: string): string => {
    const feature = features.find(f => f.id === featureId);
    return feature?.name || 'Session';
  };
  // Sync sessions with context on mount and when bookedSessions changes
  useEffect(() => {
    setSessions(prevSessions => prevSessions.map(session => ({
      ...session,
      isBooked: bookedSessions.some(bs => bs.id === session.id)
    })));
  }, [bookedSessions]);
  const days = [{
    id: 'all',
    label: 'All Days',
    date: ''
  }, {
    id: 'day1',
    label: 'Day 1',
    date: '24 Oct'
  }, {
    id: 'day2',
    label: 'Day 2',
    date: '25 Oct'
  }, {
    id: 'day3',
    label: 'Day 3',
    date: '26 Oct'
  }];
  const handleBook = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    const isCurrentlyBooked = bookedSessions.some(bs => bs.id === sessionId);
    if (isCurrentlyBooked) {
      removeBookedSession(sessionId);
    } else {
      addBookedSession(session);
      if (!isOnline) {
        setOfflineBookings(prev => prev + 1);
      }
    }
    setSessions(sessions.map(s => {
      if (s.id === sessionId) {
        return {
          ...s,
          isBooked: !isCurrentlyBooked
        };
      }
      return s;
    }));
  };
  const getSessionDay = (sessionDate: string): string => {
    if (sessionDate.includes('October 24')) return 'day1';
    if (sessionDate.includes('October 25')) return 'day2';
    if (sessionDate.includes('October 26')) return 'day3';
    return 'day1';
  };
  const parseSessionDateTime = (session: Session): Date => {
    const dateStr = session.date;
    const timeStr = session.startTime;
    const dateParts = dateStr.match(/(\w+)\s+(\d+),\s+(\d+)/);
    if (!dateParts) return new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames.indexOf(dateParts[1]);
    const day = parseInt(dateParts[2]);
    const year = parseInt(dateParts[3]);
    const timeParts = timeStr.match(/(\d+):(\d+)/);
    if (!timeParts) return new Date(year, month, day);
    const hours = parseInt(timeParts[1]);
    const minutes = parseInt(timeParts[2]);
    return new Date(year, month, day, hours, minutes);
  };
  const filteredSessions = useMemo(() => {
    let filtered = sessions.filter(session => {
      const matchesFeature = selectedFeature === 'all' || session.feature === selectedFeature;
      const matchesEvent = session.eventId === currentEvent.id;
      const matchesDay = selectedDay === 'all' || getSessionDay(session.date) === selectedDay;
      return matchesFeature && matchesEvent && matchesDay;
    });
    return filtered.sort((a, b) => {
      const dateA = parseSessionDateTime(a);
      const dateB = parseSessionDateTime(b);
      return dateA.getTime() - dateB.getTime();
    });
  }, [sessions, selectedFeature, selectedDay, currentEvent.id]);
  const bookedCount = sessions.filter(s => s.isBooked).length;
  const getTextColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return 'white';
  };
  if (showMySchedule) {
    return <MySchedule sessions={sessions} onBack={() => setShowMySchedule(false)} onCancelBooking={handleBook} data-id="element-3163" />;
  }
  return <div className="pb-24" data-id="element-3164">
      {/* Offline Booking Queue */}
      {offlineBookings > 0 && <div className="mx-4 mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2" data-id="element-3165">
          <WifiOffIcon className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-3166" />
          <div data-id="element-3167">
            <p className="text-sm font-medium text-orange-900 font-gotham" data-id="element-3168">
              {offlineBookings} booking{offlineBookings !== 1 ? 's' : ''}{' '}
              pending
            </p>
            <p className="text-xs text-orange-700 font-gotham" data-id="element-3169">
              Will sync when connection is restored
            </p>
          </div>
        </div>}

      {/* Filters - Sticky */}
      <div className="sticky top-[88px] z-30 bg-white border-b border-gray-200 pt-4 pb-3 px-4 space-y-3" data-id="element-3170">
        {/* Day Selector */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-id="element-3171">
          {days.map(day => <button key={day.id} onClick={() => setSelectedDay(day.id)} className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium font-gotham transition-all ${selectedDay === day.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} style={selectedDay === day.id ? {
          backgroundColor: currentEvent.colors.primary,
          color: getTextColor()
        } : {}} data-id="element-3172">
              <div className="text-sm" data-id="element-3173">{day.label}</div>
              {day.date && <div className="text-xs opacity-90" data-id="element-3174">{day.date}</div>}
            </button>)}
        </div>

        {/* Feature Selector */}
        <div className="relative" data-id="element-3175">
          <button onClick={() => setShowFeatureDropdown(!showFeatureDropdown)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors" data-id="element-3176">
            <div className="text-left" data-id="element-3177">
              <div className="text-xs text-gray-500 font-gotham mb-0.5" data-id="element-3178">
                Viewing
              </div>
              <div className="font-semibold text-gray-900 font-gotham" data-id="element-3179">
                {selectedFeatureData?.name || 'All Sessions'}
              </div>
            </div>
            <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${showFeatureDropdown ? 'rotate-180' : ''}`} data-id="element-3180" />
          </button>

          {showFeatureDropdown && <>
              <div className="fixed inset-0 z-40" onClick={() => setShowFeatureDropdown(false)} data-id="element-3181" />
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto" data-id="element-3182">
                {features.map(feature => <button key={feature.id} onClick={() => {
              setSelectedFeature(feature.id);
              setShowFeatureDropdown(false);
            }} className={`w-full text-left p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${selectedFeature === feature.id ? 'bg-gray-50' : ''}`} data-id="element-3183">
                    <div className="font-semibold text-gray-900 font-gotham mb-1" data-id="element-3184">
                      {feature.name}
                    </div>
                    {feature.description && <div className="text-sm text-gray-600 font-gotham line-clamp-2" data-id="element-3185">
                        {feature.description}
                      </div>}
                  </button>)}
              </div>
            </>}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between" data-id="element-3186">
        <p className="text-sm text-gray-600 font-gotham" data-id="element-3187">
          {filteredSessions.length} session
          {filteredSessions.length !== 1 ? 's' : ''} available
        </p>
        <p className="text-sm font-semibold font-gotham" style={{
        color: currentEvent.colors.primary
      }} data-id="element-3188">
          {bookedCount} scheduled
        </p>
      </div>

      {/* Session List */}
      <div className="px-4 py-4 space-y-3" data-id="element-3189">
        {filteredSessions.map(session => <SessionCard key={session.id} session={session} onBook={handleBook} onViewDetails={id => console.log('View details:', id)} featureName={getFeatureName(session.feature)} bookedLabel="Scheduled" removeLabel="Remove" data-id="element-3190" />)}
      </div>

      {/* My Schedule CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-40" data-id="element-3191">
        <button onClick={() => {
        if (onNavigateToSchedule) {
          onNavigateToSchedule();
        }
      }} className="w-full py-3 px-4 rounded-lg font-semibold font-gotham shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2" style={{
        backgroundColor: currentEvent.colors.primary,
        color: getTextColor()
      }} data-id="element-3192">
          <CalendarDaysIcon className="w-5 h-5" data-id="element-3193" />
          <span data-id="element-3194">My Schedule</span>
        </button>
      </div>
    </div>;
}